import { UserSpeakerListAPIQueryParams, assetsChaptersList, ChapterListResponse } from '@goldcast/api/content';
import { TranscriptReturn } from '@goldcast/player-config/src/types/transcript-types';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { formSpeakerDetails, populateSpeakersWithSpeakingSlots } from '@goldcast/player-config/src/utils/speakerUtils';
import { fetchTranscript } from '@goldcast/player-config/src/retrievers/fetchTranscript';
import { fetchSpeakers } from '@goldcast/player-config/src/retrievers/fetchSpeakers';
import { ChapterGenerationStatus, TranscriptContextData } from './TranscriptContextTypes';
import appLogger from '@/services/logger';
import { currentClip } from '@/stores/clip';
import { core } from '@/stores/core';
import { showErrorToast } from '@/libs/toast/toast';
import { updatePlayerStoreWithId } from '@/stores/playerV2';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

export const defaultState: TranscriptContextData = {
  transcript: [],
  end: 0,
  speakersWithDetails: {},
  usedSpeakers: [],
  timeSrtIndexArray: [],
  chapterData: undefined,
  chapterGenerationStatus: ChapterGenerationStatus.NOT_STARTED,
  chaptersLastUpdatedAt: null,
  isTranscriptInitialized: true
};

export function getSpeakersParams(contentId: string): UserSpeakerListAPIQueryParams {
  const { content } = core.getSnapshot();
  if (content && content?.media_source_type !== 'UPLOAD') {
    return { broadcast: contentId };
  }
  return { upload: contentId };
}

export function loadChapters(broadcastId: string) {
  return assetsChaptersList({
    queryParams: {
      content_id: broadcastId
    }
  })
    .then(chapters => {
      return chapters;
    })
    .catch(err => {
      appLogger.error('Error loading chapters', err);
      return {
        chapter_generation_status: ChapterGenerationStatus.NOT_STARTED,
        chapters: [],
        latest_update_at: null
      };
    }) as Promise<ChapterListResponse | any>;
}

export function initTranscript(
  cacheSuffix: string,
  eventId: string,
  broadcastId: string
): Promise<[TranscriptReturn, Record<string, SpeakerWithDetails>, ChapterListResponse]> {
  return Promise.allSettled([
    fetchTranscript({ cacheSuffix, event: eventId!, broadcast: broadcastId!, onError: showErrorToast }),
    fetchSpeakers({ queryParams: getSpeakersParams(broadcastId!), onErrorToast: showErrorToast }),
    loadChapters(broadcastId!)
  ]).then(([transcriptResult, speakersResult, chaptersResult]) => {
    if (transcriptResult.status === 'rejected') {
      throw transcriptResult.reason;
    }

    return [
      transcriptResult.value,
      speakersResult.status === 'rejected' ? {} : speakersResult.value,
      chaptersResult.status === 'rejected' ? [] : chaptersResult.value
    ];
  });
}

export const formTranscriptContextData = ({
  transcriptResult,
  sessionSpeakers,
  chapterData,
  clipId,
  broadcastId
}: {
  transcriptResult: TranscriptReturn;
  sessionSpeakers: Record<string, SpeakerWithDetails>;
  chapterData: ChapterListResponse;
  clipId: string;
  broadcastId: string;
}): TranscriptContextData => {
  const transcript = transcriptResult.transcript;

  const speakersWithEmptySpeakingSlots = formSpeakerDetails(sessionSpeakers);
  const { speakers, end } = populateSpeakersWithSpeakingSlots({
    speakers: speakersWithEmptySpeakingSlots,
    transcriptResult,
    broadcastId,
    contentMetadataStoreInstance: useContentMetadataStore
  });

  const srtIndexes: number[] = [];
  const timeSrtIndexArray: TranscriptContextData['timeSrtIndexArray'] = [];

  transcript.forEach(word => {
    if (srtIndexes.includes(word.srt_index)) {
      return;
    }
    srtIndexes.push(word.srt_index);
    timeSrtIndexArray.push({ startTime: word.start_time, srtIndex: word.srt_index });
  });

  if (clipId) {
    updatePlayerStoreWithId(clipId, { currentSrtIndex: srtIndexes[0] });
  }

  return {
    end,
    transcript,
    speakersWithDetails: speakers,
    chapterData: chapterData?.chapters,
    chaptersLastUpdatedAt: chapterData.latest_update_at,
    chapterGenerationStatus: chapterData?.chapter_generation_status,
    usedSpeakers: getUsedSpeakers(speakers),
    timeSrtIndexArray,
    isTranscriptInitialized: false
  };
};

export function isDeleted(start_time: number, clipId) {
  const clip = currentClip.getSnapshot()[clipId];
  if (!clip?.asset_metadata?.excludes) {
    return false;
  }

  return clip.asset_metadata.excludes.reduce((time, [start, end]) => {
    const t = time || start_time;
    if (start <= t && end >= t) {
      return end;
    }
    return time;
  }, 0);
}

export function getUsedSpeakers(allSpeakers: Record<string, SpeakerWithDetails>): SpeakerWithDetails[] {
  return Object.values(allSpeakers).filter(speaker => !speaker.parent_id);
}

export function getUnusedSpeakers(allSpeakers: Record<string, SpeakerWithDetails>): SpeakerWithDetails[] {
  return Object.values(allSpeakers).filter(speaker => !!speaker.parent_id);
}

function getAllCachedSuffixesAndCurrentCacheSuffixKey() {
  const allCacheSuffixes = localStorage.getItem('transcriptCacheSuffix');
  const content = core.getSnapshot().content;

  if (!content) {
    throw new Error('Content not available in core store');
  }

  const { project_id: projectId, id: contentId } = content;
  const cacheSuffixKey = `${projectId}:${contentId}`;

  return { allCacheSuffixes, cacheSuffixKey };
}

export function getNewCacheSuffix() {
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  let cacheSuffix = Math.random().toString();

  if (!allCacheSuffixes) {
    setNewCacheSuffix(cacheSuffix);
    return cacheSuffix;
  }

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes);
  const currentCacheSuffix = parsedCacheSuffixes[cacheSuffixKey];

  if (currentCacheSuffix) {
    cacheSuffix = currentCacheSuffix;
  } else {
    setNewCacheSuffix(cacheSuffix);
  }

  return cacheSuffix;
}

/**
 * Use this function to set the cache suffix for the transcript
 * This is required to invalidate the cache and fetch the latest transcript in case of MAIN/FULL transcript editing actions
 */
export function setNewCacheSuffix(cacheSuffix: string) {
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes || '{}');

  localStorage.setItem(
    'transcriptCacheSuffix',
    JSON.stringify({
      ...parsedCacheSuffixes,
      [cacheSuffixKey]: cacheSuffix
    })
  );
}

export function clearCacheSuffixForCurrentContent() {
  const content = core.getSnapshot().content;
  if (!content) {
    return;
  }
  const { allCacheSuffixes, cacheSuffixKey } = getAllCachedSuffixesAndCurrentCacheSuffixKey();

  const parsedCacheSuffixes = JSON.parse(allCacheSuffixes || '{}');

  delete parsedCacheSuffixes?.[cacheSuffixKey];

  localStorage.setItem(
    'transcriptCacheSuffix',
    JSON.stringify({
      ...parsedCacheSuffixes
    })
  );
}
